import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import type {
  Account,
  IntegrationValidationError,
  JiraProject,
  ValidateIntegrationErrorResponse,
} from '@/types';

export type {
  IntegrationValidationError,
  ValidateIntegrationErrorResponse,
  ValidationCredentials,
} from '@/types';

export interface SetupOption {
  id: string;
  name: string;
}

export interface ValidateIntegrationOptions {
  suppressErrorToast?: boolean;
}

function formatValidationErrors(errors: IntegrationValidationError[]) {
  if (errors.length === 0) {
    return {
      title: 'Validation Failed',
      description: 'An unexpected error occurred while validating credentials',
    };
  }

  if (errors.length === 1) {
    return {
      title: errors[0].title,
      description: errors[0].message,
    };
  }

  return {
    title: 'Validation Failed',
    description: errors
      .map(error => `• ${error.title}: ${error.message}`)
      .join('\n'),
  };
}

export function useValidateIntegration() {
  const [isValidating, setIsValidating] = useState(false);
  const axios = useAxios();

  const validateIntegration = useCallback(
    async ({
      provider,
      account,
      config,
      settings,
      templateId,
      suppressErrorToast = false,
    }: {
      provider?: string;
      account?: string;
      config?: Account['config'];
      settings?: Account['settings'];
      templateId?: string;
    } & ValidateIntegrationOptions): Promise<{
      projects: JiraProject[];
      isValid: boolean;
      templatesError: NonNullable<
        ValidateIntegrationErrorResponse['templates']
      >;
      validationErrors?: IntegrationValidationError[];
      organizations?: SetupOption[];
    }> => {
      if (!provider) {
        return { projects: [], isValid: false, templatesError: {} };
      }

      setIsValidating(true);
      try {
        const response = await axios.post('/integration/validate', {
          provider,
          account,
          secret: account ? undefined : config,
          settings,
        });
        return {
          projects: response.data.projects,
          isValid: true,
          templatesError: {},
          organizations: response.data.organizations,
        };
      } catch (error) {
        let toastTitle = 'Failed to validate credentials';
        let toastDescription = 'An unexpected error occurred';
        let templatesError: ValidateIntegrationErrorResponse['templates'] = {};
        let validationErrors: IntegrationValidationError[] = [];

        if (error instanceof AxiosError) {
          const responseData: ValidateIntegrationErrorResponse | undefined =
            error.response?.data;
          const { errors = [], templates = {} } = responseData ?? {};

          templatesError = templates;
          validationErrors = errors;

          if (Object.entries(templates).length > 0) {
            toastTitle = 'Missing required permissions';
            toastDescription =
              templateId && templates[templateId]
                ? templates[templateId].error
                : 'Missing required permissions';
          } else {
            const formattedErrors = formatValidationErrors(errors);
            toastTitle = formattedErrors.title;
            toastDescription = formattedErrors.description;
          }
        } else if (error instanceof Error) {
          toastTitle = 'Unexpected Error';
          toastDescription =
            'An unknown error occurred while validating credentials';
        }

        const showError = templateId ? !!templatesError[templateId] : true;

        if (showError && !suppressErrorToast) {
          toast.error(toastTitle, {
            description: toastDescription,
          });
        }

        return {
          projects: [],
          templatesError,
          validationErrors,
          isValid: false,
        };
      } finally {
        setIsValidating(false);
      }
    },
    [axios]
  );

  return {
    validateIntegration,
    isValidating,
  };
}

export default useValidateIntegration;
